import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TableLoadingOverlay } from "@/components/ui/table-loading-overlay";
import { ChartContainer } from "@/components/ui/chart";
import ReactECharts from 'echarts-for-react';
import { useTheme } from "@/contexts/ThemeContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

interface LanguageStats {
  language: string;
  tweets: number;
  likes: number;
  retweets: number;
  replies: number;
  quotes: number;
  impressions: number;
  totalEngagement: number;
  engagementRate: number;
}

interface LanguageEngagementProps {
  timeFilter?: string;
  yearFilter?: string;
  quarterFilter?: string;
}

export const LanguageEngagement: React.FC<LanguageEngagementProps> = ({ 
  timeFilter = "all", 
  yearFilter = "2025", 
  quarterFilter = "all" 
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [languageData, setLanguageData] = useState<LanguageStats[]>([]);
  const { toast } = useToast();
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  const [activeTab, setActiveTab] = useState('distribution');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        console.log("Fetching language data with filters:", { timeFilter, yearFilter, quarterFilter });
        
        // First get all KOLs with their language/region
        const { data: kolsData, error: kolsError } = await supabase
          .from('kol_db')
          .select('region, social_media_handler')
          .in('social_media_type', ['Twitter', 'X']);
          
        if (kolsError) throw kolsError;
        
        // Create a map of usernames to languages
        const userLanguageMap = new Map<string, string>();
        kolsData?.forEach(kol => {
          if (kol.social_media_handler && kol.region) {
            // Handle username format with or without @
            const username = kol.social_media_handler.startsWith('@') 
              ? kol.social_media_handler.substring(1) 
              : kol.social_media_handler;
            userLanguageMap.set(username.toLowerCase(), kol.region);
          }
        });
        
        // Build Twitter search query with filters
        let query = supabase.from('twitter_search_results').select('*');
        
        // Apply time filter
        if (timeFilter !== "all") {
          const now = new Date();
          let startDate: Date;
          
          switch (timeFilter) {
            case "1m":
              startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
              break;
            case "3m":
              startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
              break;
            case "6m":
              startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
              break;
            case "1y":
              startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
              break;
            default:
              startDate = new Date(0); // Beginning of time
          }
          
          query = query.gte('created_at', startDate.toISOString());
          console.log("Applied timeFilter:", timeFilter, "with startDate:", startDate.toISOString());
        }
        
        // Apply year filter
        if (yearFilter !== "all") {
          const year = parseInt(yearFilter);
          const startDate = new Date(year, 0, 1);
          const endDate = new Date(year + 1, 0, 1);
          
          query = query
            .gte('created_at', startDate.toISOString())
            .lt('created_at', endDate.toISOString());
          console.log("Applied yearFilter:", yearFilter, "with date range:", 
            startDate.toISOString(), "to", endDate.toISOString());
        }
        
        // Apply quarter filter
        if (quarterFilter !== "all" && yearFilter !== "all") {
          const year = parseInt(yearFilter);
          let startMonth = 0;
          let endMonth = 3;
          
          switch (quarterFilter) {
            case "Q1":
              startMonth = 0; // Jan
              endMonth = 3;   // Apr
              break;
            case "Q2":
              startMonth = 3; // Apr
              endMonth = 6;   // Jul
              break;
            case "Q3":
              startMonth = 6; // Jul
              endMonth = 9;   // Oct
              break;
            case "Q4":
              startMonth = 9; // Oct
              endMonth = 12;  // Jan of next year
              break;
          }
          
          const startDate = new Date(year, startMonth, 1);
          const endDate = new Date(year, endMonth, 1);
          
          query = query
            .gte('created_at', startDate.toISOString())
            .lt('created_at', endDate.toISOString());
          console.log("Applied quarterFilter:", quarterFilter, "with date range:",
            startDate.toISOString(), "to", endDate.toISOString());
        }
        
        // Execute the query
        const { data: tweets, error: tweetsError } = await query;
          
        if (tweetsError) throw tweetsError;
        
        console.log(`Retrieved ${tweets?.length || 0} tweets after applying filters`);
        
        // Group tweets by language and calculate metrics
        const languageMap = new Map<string, LanguageStats>();
        
        tweets?.forEach(tweet => {
          if (!tweet.author_username) return;
          
          // Try to find language for this KOL
          const username = tweet.author_username.toLowerCase();
          const language = userLanguageMap.get(username) || 'Unknown';
          
          // Get or create language data
          const langStats = languageMap.get(language) || {
            language,
            tweets: 0,
            likes: 0,
            retweets: 0,
            replies: 0,
            quotes: 0,
            impressions: 0,
            totalEngagement: 0,
            engagementRate: 0
          };
          
          // Update metrics
          langStats.tweets += 1;
          langStats.likes += tweet.like_count || 0;
          langStats.retweets += tweet.retweet_count || 0;
          langStats.replies += tweet.reply_count || 0;
          langStats.quotes += tweet.quote_count || 0;
          langStats.impressions += tweet.impression_count || 0;
          langStats.totalEngagement += (tweet.like_count || 0) + 
                                      (tweet.retweet_count || 0) + 
                                      (tweet.reply_count || 0) + 
                                      (tweet.quote_count || 0);
                                      
          languageMap.set(language, langStats);
        });
        
        // Calculate engagement rate and convert map to array
        const languageDataArray = Array.from(languageMap.values()).map(lang => {
          return {
            ...lang,
            engagementRate: lang.impressions > 0 ? 
              (lang.totalEngagement / lang.impressions) * 100 : 0
          };
        });
        
        setLanguageData(languageDataArray);
        
      } catch (error) {
        console.error("Error fetching language data:", error);
        toast({
          title: "Error",
          description: "Failed to load language engagement data",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [toast, timeFilter, yearFilter, quarterFilter]); // Add filter dependencies

  // Chart configurations for different visualization types
  const getLanguageDistributionPieOption = () => {
    const data = languageData
      .filter(language => language.language !== 'Unknown')
      .map(language => ({
        name: language.language,
        value: language.tweets
      }));
    
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} tweets ({d}%)',
        backgroundColor: isLightMode ? 'rgba(255, 255, 255, 0.95)' : 'rgba(50, 50, 50, 0.9)',
        borderColor: isLightMode ? '#d1d5db' : '#6a7985',
        textStyle: {
          color: isLightMode ? '#333333' : '#f1f1f1'
        }
      },
      legend: {
        orient: 'horizontal',
        bottom: 0,
        data: data.map(item => item.name),
        textStyle: {
          color: isLightMode ? '#333333' : '#8E9196'
        },
        formatter: '{name}',
        itemGap: 15
      },
      series: [
        {
          name: 'Language Distribution',
          type: 'pie',
          radius: '65%',
          center: ['50%', '40%'],
          data: data,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          itemStyle: {
            borderRadius: 4,
            borderColor: 'transparent', // Changed to transparent
            borderWidth: 0 // Set to 0 to ensure no borders
          },
          label: {
            show: true,
            position: 'outside',
            formatter: '{b}',
            lineHeight: 20,
            color: isLightMode ? '#333' : '#ddd',
          },
          labelLine: {
            show: true,
            length: 15,
            length2: 10,
            smooth: true,
          }
        }
      ],
      color: [
        '#6366f1', '#3b82f6', '#4ade80', '#f97316', '#f43f5e',
        '#8b5cf6', '#14b8a6', '#f59e0b', '#06b6d4', '#ec4899'
      ]
    };
  };

  const getEngagementDistributionPieOption = () => {
    const data = languageData
      .filter(language => language.language !== 'Unknown')
      .map(language => ({
        name: language.language,
        value: language.totalEngagement
      }));
    
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} engagements ({d}%)',
        backgroundColor: isLightMode ? 'rgba(255, 255, 255, 0.95)' : 'rgba(50, 50, 50, 0.9)',
        borderColor: isLightMode ? '#d1d5db' : '#6a7985',
        textStyle: {
          color: isLightMode ? '#333333' : '#f1f1f1'
        }
      },
      legend: {
        orient: 'horizontal',
        bottom: 0,
        data: data.map(item => item.name),
        textStyle: {
          color: isLightMode ? '#333333' : '#8E9196'
        },
        formatter: '{name}',
        itemGap: 15
      },
      series: [
        {
          name: 'Engagement Distribution',
          type: 'pie',
          radius: '65%',
          center: ['50%', '40%'],
          data: data,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          itemStyle: {
            borderRadius: 4,
            borderColor: 'transparent', // Changed to transparent
            borderWidth: 0 // Set to 0 to ensure no borders
          },
          label: {
            show: true,
            position: 'outside',
            formatter: '{b}',
            lineHeight: 20,
            color: isLightMode ? '#333' : '#ddd',
          },
          labelLine: {
            show: true,
            length: 15,
            length2: 10,
            smooth: true,
          }
        }
      ],
      color: [
        '#6366f1', '#3b82f6', '#4ade80', '#f97316', '#f43f5e',
        '#8b5cf6', '#14b8a6', '#f59e0b', '#06b6d4', '#ec4899'
      ]
    };
  };

  const getTweetsByLanguageBarOption = () => {
    const sortedData = [...languageData]
      .filter(language => language.language !== 'Unknown')
      .sort((a, b) => b.tweets - a.tweets);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        backgroundColor: isLightMode ? 'rgba(255, 255, 255, 0.95)' : 'rgba(50, 50, 50, 0.9)',
        borderColor: isLightMode ? '#d1d5db' : '#6a7985',
        textStyle: {
          color: isLightMode ? '#333333' : '#f1f1f1'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: sortedData.map(item => item.language),
        axisLabel: {
          rotate: 0,
          interval: 0,
          color: isLightMode ? '#333' : '#ddd',
          formatter: function(value: string) {
            return value.length > 3 ? value.split('').join('\n') : value;
          },
          margin: 14,
          verticalAlign: 'middle',
          fontSize: 12
        },
        axisLine: {
          lineStyle: {
            color: isLightMode ? '#d1d5db' : '#6a7985'
          }
        }
      },
      yAxis: {
        type: 'value',
        name: 'Number of Tweets',
        nameTextStyle: {
          color: isLightMode ? '#333' : '#ddd'
        },
        axisLabel: {
          color: isLightMode ? '#333' : '#ddd'
        },
        axisLine: {
          lineStyle: {
            color: isLightMode ? '#d1d5db' : '#6a7985'
          }
        },
        splitLine: {
          lineStyle: {
            color: isLightMode ? '#e5e7eb' : '#4b5563',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'Tweets',
          type: 'bar',
          data: sortedData.map(item => item.tweets),
          itemStyle: {
            color: '#3b82f6',
            borderRadius: [4, 4, 0, 0]
          },
          label: {
            show: true,
            position: 'top',
            color: isLightMode ? '#333' : '#ddd'
          }
        }
      ]
    };
  };

  const getEngagementRateBarOption = () => {
    const sortedData = [...languageData]
      .filter(language => language.language !== 'Unknown')
      .sort((a, b) => b.engagementRate - a.engagementRate);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params: any) {
          return `${params[0].name}: ${params[0].value.toFixed(2)}%`;
        },
        backgroundColor: isLightMode ? 'rgba(255, 255, 255, 0.95)' : 'rgba(50, 50, 50, 0.9)',
        borderColor: isLightMode ? '#d1d5db' : '#6a7985',
        textStyle: {
          color: isLightMode ? '#333333' : '#f1f1f1'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: sortedData.map(item => item.language),
        axisLabel: {
          rotate: 45,
          color: isLightMode ? '#333' : '#ddd'
        },
        axisLine: {
          lineStyle: {
            color: isLightMode ? '#d1d5db' : '#6a7985'
          }
        }
      },
      yAxis: {
        type: 'value',
        name: 'Engagement Rate (%)',
        nameTextStyle: {
          color: isLightMode ? '#333' : '#ddd'
        },
        axisLabel: {
          formatter: '{value}%',
          color: isLightMode ? '#333' : '#ddd'
        },
        axisLine: {
          lineStyle: {
            color: isLightMode ? '#d1d5db' : '#6a7985'
          }
        },
        splitLine: {
          lineStyle: {
            color: isLightMode ? '#e5e7eb' : '#4b5563',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'Engagement Rate',
          type: 'bar',
          data: sortedData.map(item => item.engagementRate),
          itemStyle: {
            color: '#4ade80',
            borderRadius: [4, 4, 0, 0]
          },
          label: {
            show: true,
            position: 'top',
            formatter: '{c}%',
            color: isLightMode ? '#333' : '#ddd'
          }
        }
      ]
    };
  };

  const getImpressionsByLanguageBarOption = () => {
    const sortedData = [...languageData]
      .filter(language => language.language !== 'Unknown')
      .sort((a, b) => b.impressions - a.impressions);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params: any) {
          return `${params[0].name}: ${params[0].value.toLocaleString()} impressions`;
        },
        backgroundColor: isLightMode ? 'rgba(255, 255, 255, 0.95)' : 'rgba(50, 50, 50, 0.9)',
        borderColor: isLightMode ? '#d1d5db' : '#6a7985',
        textStyle: {
          color: isLightMode ? '#333333' : '#f1f1f1'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: sortedData.map(item => item.language),
        axisLabel: {
          rotate: 45,
          color: isLightMode ? '#333' : '#ddd'
        },
        axisLine: {
          lineStyle: {
            color: isLightMode ? '#d1d5db' : '#6a7985'
          }
        }
      },
      yAxis: {
        type: 'value',
        name: 'Impressions',
        nameTextStyle: {
          color: isLightMode ? '#333' : '#ddd'
        },
        axisLabel: {
          color: isLightMode ? '#333' : '#ddd'
        },
        axisLine: {
          lineStyle: {
            color: isLightMode ? '#d1d5db' : '#6a7985'
          }
        },
        splitLine: {
          lineStyle: {
            color: isLightMode ? '#e5e7eb' : '#4b5563',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'Impressions',
          type: 'bar',
          data: sortedData.map(item => item.impressions),
          itemStyle: {
            color: '#f97316',
            borderRadius: [4, 4, 0, 0]
          },
          label: {
            show: true,
            position: 'top',
            formatter: function(params: any) {
              const value = params.value;
              if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + 'M';
              } else if (value >= 1000) {
                return (value / 1000).toFixed(1) + 'K';
              }
              return value;
            },
            color: isLightMode ? '#333' : '#ddd'
          }
        }
      ]
    };
  };

  // Summary stats
  const totalTweets = languageData.reduce((sum, lang) => sum + lang.tweets, 0);
  const totalEngagements = languageData.reduce((sum, lang) => sum + lang.totalEngagement, 0);
  const totalImpressions = languageData.reduce((sum, lang) => sum + lang.impressions, 0);
  const overallEngagementRate = totalImpressions > 0 ? (totalEngagements / totalImpressions) * 100 : 0;

  return (
    <Card className="bg-card border border-border rounded-md shadow-sm h-full">
      <CardHeader className="border-b border-border pb-3">
        <CardTitle>Twitter Language Analytics</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <TableLoadingOverlay isLoading={isLoading} message="Loading language data..." />
        ) : languageData.length > 0 ? (
          <div className="flex flex-col h-full">
            {/* Summary statistics */}
            <div className="grid grid-cols-4 gap-2 p-4 border-b border-border">
              <div className="flex flex-col items-center justify-center p-2 rounded-md bg-blue-50 dark:bg-blue-900/20">
                <span className="text-sm text-muted-foreground">Total Tweets</span>
                <span className="text-2xl font-bold">{totalTweets.toLocaleString()}</span>
              </div>
              <div className="flex flex-col items-center justify-center p-2 rounded-md bg-green-50 dark:bg-green-900/20">
                <span className="text-sm text-muted-foreground">Total Engagements</span>
                <span className="text-2xl font-bold">{totalEngagements.toLocaleString()}</span>
              </div>
              <div className="flex flex-col items-center justify-center p-2 rounded-md bg-orange-50 dark:bg-orange-900/20">
                <span className="text-sm text-muted-foreground">Total Impressions</span>
                <span className="text-2xl font-bold">{totalImpressions.toLocaleString()}</span>
              </div>
              <div className="flex flex-col items-center justify-center p-2 rounded-md bg-purple-50 dark:bg-purple-900/20">
                <span className="text-sm text-muted-foreground">Engagement Rate</span>
                <span className="text-2xl font-bold">{overallEngagementRate.toFixed(2)}%</span>
              </div>
            </div>

            <Tabs 
              value={activeTab} 
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="w-full justify-start rounded-none mb-0 px-4 py-2">
                <TabsTrigger value="distribution">Language Distribution</TabsTrigger>
                <TabsTrigger value="engagement">Engagement Metrics</TabsTrigger>
              </TabsList>
              
              <TabsContent value="distribution" className="mt-0">
                <div className="flex flex-col gap-4 p-4">
                  {/* Pie chart */}
                  <div className="h-[400px]">
                    <ChartContainer
                      config={{
                        tweets: { color: '#3b82f6' },
                      }}
                      className="w-full h-full"
                    >
                      <ReactECharts 
                        option={getLanguageDistributionPieOption()}
                        style={{ height: '100%', width: '100%' }}
                        opts={{ renderer: 'canvas' }}
                      />
                    </ChartContainer>
                  </div>
                  
                  {/* Bar chart - now below the pie chart */}
                  <div className="h-[400px]">
                    <ChartContainer
                      config={{
                        tweets: { color: '#3b82f6' },
                      }}
                      className="w-full h-full"
                    >
                      <ReactECharts 
                        option={getTweetsByLanguageBarOption()}
                        style={{ height: '100%', width: '100%' }}
                        opts={{ renderer: 'canvas' }}
                      />
                    </ChartContainer>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="engagement" className="mt-0">
                <div className="flex flex-col gap-4 p-4">
                  <div className="h-[400px]">
                    <ChartContainer
                      config={{
                        engagement: { color: '#4ade80' },
                      }}
                      className="w-full h-full"
                    >
                      <ReactECharts 
                        option={getEngagementDistributionPieOption()}
                        style={{ height: '100%', width: '100%' }}
                        opts={{ renderer: 'canvas' }}
                      />
                    </ChartContainer>
                  </div>
                  
                  {/* Modified: Place Impressions and Engagement Rate charts side-by-side in a grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="h-[400px]">
                      <ChartContainer
                        config={{
                          impressions: { color: '#f97316' },
                        }}
                        className="w-full h-full"
                      >
                        <ReactECharts 
                          option={getImpressionsByLanguageBarOption()}
                          style={{ height: '100%', width: '100%' }}
                          opts={{ renderer: 'canvas' }}
                        />
                      </ChartContainer>
                    </div>
                    <div className="h-[400px]">
                      <ChartContainer
                        config={{
                          engagementRate: { color: '#4ade80' },
                        }}
                        className="w-full h-full"
                      >
                        <ReactECharts 
                          option={getEngagementRateBarOption()}
                          style={{ height: '100%', width: '100%' }}
                          opts={{ renderer: 'canvas' }}
                        />
                      </ChartContainer>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="flex justify-center items-center p-12">
            <p className="text-muted-foreground">No language data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
